import { WebComponent } from '@cian/mf-registry/browser';
import * as React from 'react';
import * as ReactDOM from 'react-dom/client';

import { IApplicationContext } from '../../shared/types/applicationContext';
import { IReduxStore } from '../../shared/types/redux';
import { Application } from '../../shared/containers/App';
import { V3_RECOMMENDATIONS_RUNTIME_NAME } from '../../shared/constants/runtimeNames';

interface IHydrationStatusProps {
  children: React.ReactElement;
  onHydrated(): void;
}

const HydrationStatus: React.FC<IHydrationStatusProps> = ({ children, onHydrated }) => {
  React.useEffect(() => {
    onHydrated();
  }, [onHydrated]);

  return children;
};

export function createCustomElementConstructor(
  context: IApplicationContext,
  store: IReduxStore,
): CustomElementConstructor {
  class Recommendations extends WebComponent {
    private applicationContext: IApplicationContext;
    private reactRoot?: ReactDOM.Root;
    private isHydrating = false;
    private pendingReactElement?: React.ReactElement;

    protected microfrontendName = context.config.getStrict<string>('projectName');
    protected imageVersion = '';
    protected runtimeName = V3_RECOMMENDATIONS_RUNTIME_NAME;

    public api = {};

    private handleHydrated = () => {
      const pendingReactElement = this.pendingReactElement;

      this.isHydrating = false;
      this.pendingReactElement = undefined;

      if (pendingReactElement && this.reactRoot) {
        this.reactRoot.render(pendingReactElement);
      }
    };

    public async connectedCallback(): Promise<void> {
      const instanceId = this.getAttribute('instance') || undefined;
      this.applicationContext = { ...context, config: context.config.attach({ instanceId }) };
      this.imageVersion = this.applicationContext.config.getStrict<string>('version.commit');
      super.connectedCallback();
    }

    public async disconnectedCallback() {
      this.reactRoot?.unmount();
      this.reactRoot = undefined;
      this.isHydrating = false;
      this.pendingReactElement = undefined;
      super.disconnectedCallback();
    }

    public async render() {
      const reactElement: React.JSX.Element = (
        <Application {...this.props} context={this.applicationContext} reduxStore={store} />
      );

      if (this.reactRoot) {
        if (this.isHydrating) {
          this.pendingReactElement = reactElement;

          return;
        }

        this.reactRoot.render(reactElement);

        return;
      }

      if (this.shouldHydrate) {
        this.isHydrating = true;
        this.reactRoot = ReactDOM.hydrateRoot(
          this,
          <HydrationStatus onHydrated={this.handleHydrated}>{reactElement}</HydrationStatus>,
        );
      } else {
        this.reactRoot = ReactDOM.createRoot(this);
        this.reactRoot.render(reactElement);
      }
    }
  }

  return Recommendations;
}
